import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Save, Loader2, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";

/**
 * Reusable hook that provides an editable draft copy of the current settings
 * along with save/reset helpers. Section-scoped for convenience.
 */
export function useSettingsEditor() {
  const { settings, setSettings, applyTheme } = useSettings();
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (settings) setDraft(JSON.parse(JSON.stringify(settings)));
  }, [settings]);

  const patchSection = (key, value) => {
    setDraft((d) => ({ ...(d || {}), [key]: value }));
  };

  const patchPath = (path, value) => {
    setDraft((d) => {
      if (!d) return d;
      const next = JSON.parse(JSON.stringify(d));
      const parts = path.split(".");
      let cur = next;
      for (let i = 0; i < parts.length - 1; i++) {
        cur[parts[i]] = cur[parts[i]] ?? {};
        cur = cur[parts[i]];
      }
      cur[parts[parts.length - 1]] = value;
      return next;
    });
  };

  const save = useCallback(async () => {
    if (!draft) return;
    setSaving(true);
    try {
      const { data } = await api.put("/settings", draft);
      setSettings(data);
      applyTheme?.(data?.theme);
      toast.success("Saved");
      return data;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  }, [draft, setSettings, applyTheme]);

  const reset = () => {
    if (settings) setDraft(JSON.parse(JSON.stringify(settings)));
  };

  return { draft, setDraft, patchSection, patchPath, save, reset, saving };
}

export function SaveBar({ onSave, onReset, saving }) {
  return (
    <div className="sticky bottom-0 -mx-4 sm:-mx-6 lg:-mx-8 mt-10 px-4 sm:px-6 lg:px-8 py-4 bg-white/80 backdrop-blur border-t border-slate-200 flex items-center justify-end gap-3 z-20">
      <Button
        type="button"
        variant="outline"
        className="rounded-full h-10 border-slate-200"
        onClick={onReset}
        data-testid="cms-reset-btn"
      >
        <RotateCcw className="w-4 h-4 mr-2" /> Revert
      </Button>
      <Button
        type="button"
        style={{ backgroundColor: "var(--brand-primary)" }}
        className="rounded-full h-10 text-white hover:brightness-110 transition-all px-5"
        disabled={saving}
        onClick={onSave}
        data-testid="cms-save-btn"
      >
        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
        Save Changes
      </Button>
    </div>
  );
}
